/**
 * Toasts: what the player must act on or cares about as "transmission" cards on the right, off the
 * alert line (which keeps only what is urgent: inbound, our critical capital, the release countdown,
 * the strike offer, order feedback). At most 2 on screen, newest on top; offers to us are pinned
 * cards with buttons until answered or expired. Nothing is lost: when the stack is full a newcomer
 * waits (higher priority first) and the oldest news card that has been up `MIN_SHOW_MS` makes way;
 * a burst of the same news merges into one card (`×3`), and a wave of damage is one card rewritten
 * as it grows (`upsert`).
 *
 * Kinds: `news` (a line of what happened), `offer` (answerable, pinned), `moment` (a big
 * dramatic beat — betrayal and the like: louder card, top priority, never dropped unseen) and
 * `chat` (a player's message, later). Pure apart from the clock passed in (test/toasts.test.ts).
 */
import { BALANCE, TICK_HZ, type PlayerId, type PlayerView } from '@sa/sim';
import { clock } from './alerts';
import { incomingOffers, type DiplomacyBook, type IncomingOffer } from './diplomacy';

export type ToastTone = 'info' | 'ok' | 'ally' | 'warn' | 'danger';
export type ToastKind = 'news' | 'offer' | 'moment' | 'chat';

export interface ToastButton {
  key: string | null;
  label: string;
  run(): void;
}

/** What to say: the card's header (`title`: OFFER, NEWS, DAMAGE, DEFENCE, STRIKE, BETRAYAL, CHAT) and its line. */
export interface ToastSpec {
  title: string;
  text: string;
  tone: ToastTone;
  /** the nation it comes from / is about: its colour and pattern on the card */
  from?: PlayerId;
  kind?: ToastKind;
  /** a second, dimmer line */
  sub?: string;
  /** a full stack lets the higher first (default by tone; a moment 9) */
  priority?: number;
  /** on screen this long once shown (ms) */
  ms?: number;
  /** dropped if it waited unseen this long (ms; a moment never is) */
  maxWaitMs?: number;
  /** bursts of the same key merge (default: the text) */
  key?: string;
  /** `chat` only: buttons on the card (a message's map point: `[V5] LOOK`) */
  buttons?: ToastButton[];
}

/** One card as drawn (news from the queue, or an offer). */
export interface ToastCard {
  id: string;
  kind: ToastKind;
  title: string;
  text: string;
  sub?: string;
  tone: ToastTone;
  from?: PlayerId;
  /** a burst merged into this card */
  count: number;
  /** life left, 1 → 0 (the draining line) */
  life: number;
  /** a countdown beside the title (offers: `0:24`) */
  timer?: string;
  buttons?: ToastButton[];
  /** shown as one compact line under a newer card */
  compact: boolean;
}

export interface ToastFrame {
  cards: ToastCard[];
  /** cards that appeared this frame (a sound each) */
  entered: ToastCard[];
}

export const MAX_TOASTS = 2;
/** a card stays at least this long before a newcomer may push it out (ms) */
export const MIN_SHOW_MS = 2_500;
/** the same news this soon again merges into the card (ms) */
export const DEDUPE_MS = 4_000;
const SHOW_MS: Record<ToastKind, number> = { news: 6_000, offer: Infinity, moment: 9_000, chat: 8_000 };
const WAIT_MS = 45_000;
const TONE_PRIORITY: Record<ToastTone, number> = { danger: 4, warn: 3, ally: 2, ok: 2, info: 1 };

interface Item {
  id: number;
  spec: ToastSpec;
  kind: ToastKind;
  key: string;
  priority: number;
  ms: number;
  maxWaitMs: number;
  bornMs: number;
  /** when it went up (null: waiting) */
  shownAt: number | null;
  count: number;
}

export class ToastQueue {
  private items: Item[] = [];
  private nextId = 1;
  /** card ids on screen last frame (to tell which entered) */
  private onScreen = new Set<string>();

  /** Queue a toast (or merge it into the same news still up or waiting). */
  push(spec: ToastSpec, nowMs: number): void {
    const key = spec.key ?? spec.text;
    const same = this.items.find((i) => i.key === key && nowMs - (i.shownAt ?? i.bornMs) < DEDUPE_MS);
    if (same) {
      same.count++;
      // up already: its time starts over (no new reveal)
      if (same.shownAt !== null) same.shownAt = nowMs;
      return;
    }
    const kind = spec.kind ?? 'news';
    this.items.push({
      id: this.nextId++,
      spec,
      kind,
      key,
      priority: spec.priority ?? (kind === 'moment' ? 9 : TONE_PRIORITY[spec.tone]),
      ms: spec.ms ?? SHOW_MS[kind],
      maxWaitMs: spec.maxWaitMs ?? (kind === 'moment' ? Infinity : WAIT_MS),
      bornMs: nowMs,
      shownAt: null,
      count: 1,
    });
  }

  /**
   * The card for `spec.key` says this now (a wave of damage growing: `RADAR, SAM ×2 LOST`): rewritten
   * in place — up already, its time starts over — or queued when there is none.
   */
  upsert(spec: ToastSpec & { key: string }, nowMs: number): void {
    const same = this.items.find((i) => i.key === spec.key);
    if (!same) return this.push(spec, nowMs);
    same.spec = spec;
    same.priority = spec.priority ?? TONE_PRIORITY[spec.tone];
    if (same.shownAt !== null) same.shownAt = nowMs;
  }

  /** A card with this key is queued or up. */
  has(key: string): boolean {
    return this.items.some((i) => i.key === key);
  }

  /** Drop every toast whose key matches (e.g. a betrayal made moot by a new alliance). */
  drop(pred: (spec: ToastSpec) => boolean): void {
    this.items = this.items.filter((i) => !pred(i.spec));
  }

  /** Toasts queued or on screen (tests, dev). */
  all(): readonly ToastSpec[] {
    return this.items.map((i) => i.spec);
  }

  /**
   * The stack at `nowMs`: `pinned` cards (offers) first and never pushed out, then the news cards,
   * newest first — the newest full, older ones compact.
   */
  frame(nowMs: number, pinned: readonly ToastCard[] = []): ToastFrame {
    // time's up, or waited too long unseen
    this.items = this.items.filter((i) => (i.shownAt === null ? nowMs - i.bornMs < i.maxWaitMs : nowMs - i.shownAt < i.ms));
    const up = () => this.items.filter((i) => i.shownAt !== null);
    const waiting = this.items.filter((i) => i.shownAt === null).sort((a, b) => b.priority - a.priority || a.bornMs - b.bornMs);
    const room = Math.max(0, MAX_TOASTS - pinned.length);
    for (const w of waiting) {
      const shown = up();
      if (shown.length >= room) {
        // the oldest card that had its minimum time makes way (a lower-priority one first)
        const out = shown
          .filter((i) => nowMs - i.shownAt! >= MIN_SHOW_MS && i.priority <= w.priority)
          .sort((a, b) => a.priority - b.priority || a.shownAt! - b.shownAt!)[0];
        if (!out) break;
        this.items = this.items.filter((i) => i !== out);
      }
      w.shownAt = nowMs;
    }
    const news = up()
      .sort((a, b) => b.shownAt! - a.shownAt! || b.id - a.id)
      .map(
        (i, k): ToastCard => ({
          id: `t${i.id}`,
          kind: i.kind,
          title: i.spec.title,
          text: i.spec.text,
          ...(i.spec.sub ? { sub: i.spec.sub } : {}),
          tone: i.spec.tone,
          ...(i.spec.from !== undefined ? { from: i.spec.from } : {}),
          count: i.count,
          life: Number.isFinite(i.ms) ? Math.max(0, 1 - (nowMs - i.shownAt!) / i.ms) : 1,
          // a moment never folds; the newest news card is full when no offer is pinned above it
          compact: i.kind !== 'moment' && (k > 0 || pinned.length > 0),
          ...(i.kind === 'chat' && i.spec.buttons?.length ? { buttons: i.spec.buttons } : {}),
        }),
      );
    const cards = [...pinned, ...news];
    const entered = cards.filter((c) => !this.onScreen.has(c.id));
    this.onScreen = new Set(cards.map((c) => c.id));
    return { cards, entered };
  }
}

/** How long an offer stands (ticks), for its draining line. */
const offerTicks = (o: IncomingOffer) => (o.kind === 'ally' ? BALANCE.alliance.requestTicks : BALANCE.diplomacy.ceasefireRequestTicks);

/** The offer the card shows at `page` (it pages through the open offers to us), or null. */
export function shownOffer(view: PlayerView, book: Pick<DiplomacyBook, 'isAnswered'>, page: number): { offer: IncomingOffer; index: number; count: number } | null {
  const list = incomingOffers(view, book);
  if (!list.length) return null;
  const index = ((page % list.length) + list.length) % list.length;
  return { offer: list[index], index, count: list.length };
}

/**
 * The open offers to us as ONE pinned card (so a news slot always stays free): the offer at
 * `page` — `HALCYON PROPOSES ALLIANCE` with its time left and [Y ACCEPT] [N DECLINE] (Y / N answer
 * the offer shown) — and, with two or more, `OFFER 1/2` in the header and a [› NEXT] pager
 * (`next`). The line is drawn at once (no typing: it must be read). A spectator gets none.
 */
export function offerCards(
  view: PlayerView,
  book: Pick<DiplomacyBook, 'isAnswered'>,
  nameOf: (p: PlayerId) => string,
  answer: (o: IncomingOffer, accept: boolean) => void,
  page = 0,
  next: () => void = () => {},
): ToastCard[] {
  const shown = shownOffer(view, book, page);
  if (!shown) return [];
  const { offer: o, index, count } = shown;
  const left = Math.max(0, o.expiresTick - view.tick);
  const what = o.kind === 'ally' ? 'ALLIANCE' : `CEASEFIRE ${clock(BALANCE.diplomacy.ceasefireTicks / TICK_HZ)}`;
  return [
    {
      // (several offers: one card that pages; its words change in place)
      id: count > 1 ? 'offers' : `offer:${o.kind}:${o.from}:${o.expiresTick}`,
      kind: 'offer',
      title: count > 1 ? `OFFER ${index + 1}/${count}` : 'OFFER',
      text: `${nameOf(o.from)} PROPOSES ${what}`,
      tone: 'ally',
      from: o.from,
      count: 1,
      life: Math.min(1, left / offerTicks(o)),
      timer: clock(left / TICK_HZ),
      compact: false,
      buttons: [
        { key: 'y', label: 'ACCEPT', run: () => answer(o, true) },
        { key: 'n', label: 'DECLINE', run: () => answer(o, false) },
        ...(count > 1 ? [{ key: null, label: '› NEXT', run: next }] : []),
      ],
    },
  ];
}
